/**
 * Hook de acesso aos módulos do plano contratado
 *
 * Lê o resumo da assinatura em /auth/me (enabledModules: null = todos)
 * com cache por usuário, compartilhado entre menu, rotas e avisos.
 *
 * @author Equipe VendeAi
 * @since 1.3.0
 */

import { useEffect, useState } from 'react';
import { api } from '@services/api';
import { useAuth } from '@hooks/useAuth';

export type PlanModuleKey =
  | 'tables'
  | 'whatsapp'
  | 'inventory'
  | 'financial'
  | 'fiscal'
  | 'loyalty'
  | 'hr'
  | 'bankReconciliation'
  | 'abandonedCarts';

export const PLAN_MODULE_LABELS: Record<PlanModuleKey, string> = {
  tables: 'Mesas e QR Code',
  whatsapp: 'WhatsApp e leads',
  inventory: 'Estoque, ficha técnica e fornecedores',
  financial: 'Financeiro',
  fiscal: 'Nota fiscal',
  loyalty: 'Fidelidade e campanhas',
  hr: 'RH e ponto eletrônico',
  bankReconciliation: 'Conciliação bancária',
  abandonedCarts: 'Carrinho abandonado',
};

export interface SubscriptionSummary {
  status: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'EXPIRED' | 'CANCELLED';
  endsAt: string | null;
  daysLeft: number | null;
  plan: { id: string; name: string; slug: string; isTrial: boolean; priceMonthly: number } | null;
  enabledModules: PlanModuleKey[] | null;
}

let cache: { userId: string; promise: Promise<SubscriptionSummary | null> } | null = null;

function loadSummary(userId: string): Promise<SubscriptionSummary | null> {
  if (!cache || cache.userId !== userId) {
    cache = {
      userId,
      promise: api
        .get('/auth/me')
        .then(res => (res.data?.data?.subscription ?? null) as SubscriptionSummary | null)
        .catch(() => {
          cache = null;
          return null;
        }),
    };
  }
  return cache.promise;
}

/** Descarta o cache (ex.: após contratar/cancelar um plano) */
export function invalidatePlanAccess(): void {
  cache = null;
}

export function usePlanAccess() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(!!user);

  useEffect(() => {
    if (!user || user.role === 'SUPER_ADMIN') {
      setSummary(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    loadSummary(user.id).then(data => {
      if (active) {
        setSummary(data);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [user]);

  const enabledModules = summary?.enabledModules ?? null;
  // Enquanto carrega ou sem dados, não bloqueia (o backend é a fonte da verdade)
  const hasModule = (module: PlanModuleKey): boolean =>
    loading || enabledModules === null || enabledModules.includes(module);

  return { summary, loading, enabledModules, hasModule };
}
